import { useMutation } from '@tanstack/react-query';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { useOrganizationId } from './useOrganization';
import { onPersistFilesToClientProfiles } from './cacheKeys';
import { APITypes } from '../../primary-record-types';

const intl = getIntl('UsePersistFileToClientProfiles', {
    success: 'Successfully shared files with clients',
    successSingular: 'Successfully shared files with client',
    error: 'Failed to send file to clients',
    failedToSendToClients:
        'Failed to send file to {count} client(s).  Your organization may not have proper permissions to some of the selected clients.',
});

export type UsePersistFileToClientProfilesArgs = {
    onPersistSuccess?: () => void;
};

export type PersistFileToClientProfilesArgs =
    APITypes.organizationService.PersistFilesToOrganizationMedicalProfilesRequest;

export type UsePersistFileToClientProfilesReturnValue = {
    persistFilesToClientProfiles: (args: PersistFileToClientProfilesArgs) => void;
    isPersisting: boolean;
};

export default function usePersistFileToClientProfiles({
    onPersistSuccess,
}: UsePersistFileToClientProfilesArgs = {}): UsePersistFileToClientProfilesReturnValue {
    const notify = useNotify();
    const organizationId = useOrganizationId();
    const persistMutation = useMutation({
        mutationFn: async (args: PersistFileToClientProfilesArgs) => {
            return appAPI.post<APITypes.organizationService.PersistFilesToOrganizationMedicalProfilesResponse>(
                urls.api.organizations.files.persistToMedicalProfiles(organizationId),
                {
                    body: args,
                },
            );
        },
        onSuccess: (data, args) => {
            onPersistFilesToClientProfiles.resetCache({ clientProfileIds: args.destinationMedicalProfileIds })();
            if (data.noAccessMedicalProfileIds?.length) {
                notify('warn', intl('failedToSendToClients', { count: data.noAccessMedicalProfileIds.length }));
            } else {
                notify('success', intl(args.destinationMedicalProfileIds.length === 1 ? 'successSingular' : 'success'));
            }
            onPersistSuccess?.();
        },
        onError: () => {
            notify('error', intl('error'));
        },
    });

    return {
        persistFilesToClientProfiles: persistMutation.mutate,
        isPersisting: persistMutation.isPending,
    };
}
